import { DataTable } from 'primereact/datatable';
import '../assets/entity-list.css';
import { SessionEventDto, SessionSearchDto } from '../general/dtos';
import { useEffect, useState } from 'react';
import { Column } from 'primereact/column';
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism"
import { bookmarkEventKey, useBookmarkSelection } from '../general/bookmark-selection';

interface ActivityDetailsProps {
    selectedSession: SessionSearchDto | null;
    loadEvents: (sub: string, sessionId: string) => Promise<SessionEventDto[]>;
}

export function ActivityDetails({
    selectedSession,
    loadEvents,
}: ActivityDetailsProps) {
  const [events, setEvents] = useState<SessionEventDto[]>([]);
  const [eventTime, setEventTime] = useBookmarkSelection('event');
  const selectedEvent = events.find(event => bookmarkEventKey(event) === eventTime) ?? null;
  const setSelectedEvent = (event: SessionEventDto | null) => setEventTime(event ? bookmarkEventKey(event) : null);
  const [selectedPayload, setSelectedPayload] = useState<string>('');
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);

  // Load events when session changes
  useEffect(() => {
    setEvents([]);
    setSelectedPayload('');
    if (!selectedSession) {
        return;
    }

    if (selectedSession.event_count > 0) {
        let active = true;
        setLoadingEvents(true);
        loadEvents(selectedSession.sub, selectedSession.session_id).then((data) => {
            if (!active) return;
            setEvents(data);
            setLoadingEvents(false);
        });
        return () => { active = false; };
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession]);

  const formatPayload = (payload: string | null): string => {
    const result: string = payload ?? '';
    if (result.length > 2 && result[0] === '{' && result[result.length - 1] === '}') {
        try {
            const o: any = JSON.parse(result);
            return JSON.stringify(o, null, 2);
        } catch (error) {
            return result;
        }
    }
    return result;
  }

  useEffect(() => {
    setSelectedPayload(formatPayload(selectedEvent?.payload!));
  }, [selectedEvent]);

  const isJson: boolean = (selectedPayload.length > 2 && selectedPayload[0] === '{' && selectedPayload[selectedPayload.length - 1] === '}');
  
  return (<>
    <div className="flex w-full" style={{ height: "100%" }}>
        <div className="w-6 pr-2" style={{ height: "100%" }}>
            <DataTable
                value={events}
                key={'occured_at'}
                loading={loadingEvents}
                selectionMode="single"
                selection={selectedEvent}
                onSelectionChange={(e) => setSelectedEvent(e.value as SessionEventDto)}
                scrollable
                scrollHeight="100%"
                size="small"
                emptyMessage={<div className="flex align-items-center gap-2"><i className="pi pi-info-circle" /><span>No activity recorded</span></div>}
            >
                <Column field="occured_at" header="Timestamp" />
                <Column header="Payload" body={(row) => row.payload.slice(0, 40) + "…"} />
            </DataTable>
        </div>

        <div className="w-6 pl-2" style={{ height: "100%", display:'flex', flex:'1', flexDirection: 'column', minWidth: '0', minHeight: '0', overflowY: 'hidden' }}>
            <h4>Event payload</h4>
            { isJson ? (<>
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <SyntaxHighlighter language="json" style={oneDark} customStyle={{ margin: 0, height: "100%", overflow: 'auto' }}>
                        {selectedPayload}
                    </SyntaxHighlighter>
                </div></>) : (<>
                    <textarea
                        readOnly
                        value={selectedPayload}
                        style={{
                            minWidth: '0',
                            minHeight: '0',
                            width: "100%",
                            flex: '1',
                            height: "100%",
                            overflow: "scroll",
                            resize: "none",
                            padding: "1rem",
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                            whiteSpace: "pre-wrap",
                            background: "var(--surface-ground)",
                            border: "1px solid var(--surface-border)",
                            borderRadius: "4px"
                        }}
                    />
                </>)
            }
        </div>
    </div></>);
}
